// src/pages/Notifications.js
import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Bell, Filter, CheckCheck, RefreshCw, Inbox } from "lucide-react";
import useNotifications from "../hooks/useNotifications";
import NotificationList from "../components/NotificationList";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import * as notificationsService from "../services/notificationsService";
import { useAuth } from "../hooks/useAuth";
import { normalizeNotificationType, NOTIFICATION_TYPES } from "../config/notificationConfig";
import { errorDev } from '../utils/logger.js';

// Filter options for notifications
const FILTER_OPTIONS = [
  { id: 'all', label: 'All', labelKey: 'notifications.filterAll' },
  { id: 'unread', label: 'Unread', labelKey: 'notifications.filterUnread' },
  { id: 'offers', label: 'Offers', labelKey: 'notifications.filterOffers' },
  { id: 'system', label: 'System', labelKey: 'notifications.filterSystem' },
];

const Notifications = () => {
  const { t, ready: i18nReady } = useTranslation(['common']);
  const { notifications, loading, error, refresh } = useNotifications();
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState('all');
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  React.useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Safe translation function
  const safeT = useCallback((key, fallback = key) => {
    if (!i18nReady) return fallback;
    try {
      const result = t(key);
      return result === key ? fallback : result;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback;
    }
  }, [i18nReady, t]);

  // Filter notifications based on active filter
  const visibleNotifications = useMemo(
    () => (notifications || []).filter((n) => notificationsService.isNotificationSubscribed(n)),
    [notifications]
  );

  const filteredNotifications = useMemo(() => {
    if (activeFilter === 'all') return visibleNotifications;
    
    return visibleNotifications.filter(n => {
      const type = normalizeNotificationType(n.type);
      
      switch (activeFilter) {
        case 'unread':
          return notificationsService.isNotificationUnread(n);
        case 'offers':
          return type === NOTIFICATION_TYPES.OFFER_RECEIVED ||
                 type === NOTIFICATION_TYPES.OFFER_ACCEPTED ||
                 type === NOTIFICATION_TYPES.OFFER_DECLINED ||
                 type === NOTIFICATION_TYPES.OFFER_UPDATE;
        case 'system':
          return type === NOTIFICATION_TYPES.SYSTEM ||
                 type === NOTIFICATION_TYPES.INFO ||
                 type === NOTIFICATION_TYPES.POINTS_EARNED;
        default:
          return true;
      }
    });
  }, [visibleNotifications, activeFilter]);

  // Count unread notifications
  const unreadCount = useMemo(() => {
    return visibleNotifications.filter((n) => notificationsService.isNotificationUnread(n)).length;
  }, [visibleNotifications]);

  // Mark all as read
  const handleMarkAllRead = useCallback(async () => {
    if (!user || isMarkingAll || unreadCount === 0) return;

    setIsMarkingAll(true);
    try {
      await notificationsService.markAllNotificationsRead(user.uid);
      refresh?.();
    } catch (err) {
      // Silent fail - will sync on next refresh
    errorDev("Operation failed:", err);
    } finally {
      setIsMarkingAll(false);
    }
  }, [user, isMarkingAll, unreadCount, refresh]);

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <div className="sticky top-0 z-30 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
          <div className="max-w-2xl mx-auto px-4">
            <div className="h-12 flex items-center gap-2">
              <Bell className="w-5 h-5 text-green-700 dark:text-green-400" />
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {safeT('notifications.title', 'Notifications')}
              </h1>
            </div>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-800 p-10">
            <LoadingSpinner text={safeT('common:loading', 'Loading...')} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <div className="sticky top-0 z-30 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-2xl mx-auto px-4">
          <div className="h-12 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-green-700 dark:text-green-400" />
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {safeT('notifications.title', 'Notifications')}
              </h1>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={isMarkingAll}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-green-700 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-50"
              >
                {isMarkingAll ? <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" /> : <CheckCheck className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{safeT('notifications.markAllRead', 'Mark all read')}</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 pb-2.5 overflow-x-auto no-scrollbar">
            <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => setActiveFilter(option.id)}
                className={`
                  px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap
                  ${activeFilter === option.id
                    ? 'bg-green-700 text-white border-green-700 dark:bg-green-600 dark:border-green-600'
                    : 'bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-neutral-700 hover:border-green-500'
                  }
                `}
              >
                {safeT(option.labelKey, option.label)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 sm:py-6">
        {!isOnline && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            It's Orrith, not you. You are offline, and some updates may be delayed.
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
            <div>It's Orrith, not you. We could not refresh notifications right now.</div>
            <button
              onClick={() => refresh?.()}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:underline dark:text-red-200"
            >
              <RefreshCw className="w-3 h-3" />
              Retry
            </button>
          </div>
        )}

        {/* Notification List */}
        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-800 overflow-hidden">
          {filteredNotifications.length === 0 ? (
            <div className="py-16 text-center">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-muted">
                <Inbox className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">
                {activeFilter === 'all' 
                  ? safeT('notifications.emptyAll', 'No notifications yet')
                  : safeT('notifications.emptyFiltered', 'No matching notifications')
                }
              </h3>
              <p className="text-sm text-muted-foreground">
                {activeFilter === 'all'
                  ? safeT('notifications.emptyAllDesc', 'You are all caught up!')
                  : safeT('notifications.emptyFilteredDesc', 'Try a different filter')
                }
              </p>
              {activeFilter !== 'all' && (
                <button
                  onClick={() => setActiveFilter('all')}
                  className="mt-4 px-4 py-2 text-sm font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                >
                  {safeT('notifications.viewAll', 'View all notifications')}
                </button>
              )}
            </div>
          ) : (
            <NotificationList 
              notifications={filteredNotifications} 
              showHeader={false}
              isCompact={false}
            />
          )}
        </div>

        {/* Stats footer */}
        {visibleNotifications.length > 0 && (
          <div className="mt-4 text-center text-xs text-muted-foreground">
            {safeT('notifications.total', 'Total')}: {visibleNotifications.length} {safeT('notifications.notifications', 'notifications')}
            {filteredNotifications.length !== visibleNotifications.length && (
              <span> ({filteredNotifications.length} {safeT('notifications.shown', 'shown')})</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
